/**
 * Obligation summary — product-language renderer (Tomas persona).
 * V6-6-7.
 *
 * Plain English, no statute citations in the primary view. Designed
 * for an engineering lead who needs to understand what the obligations
 * mean in practical terms without reading legal references.
 */

import { Badge, Card, EmptyState } from '@aigovops/design-system';

// ============================================================
// Types — shared obligation set payload
// ============================================================

export interface ObligationEntry {
  obligation_ref: string;
  framework: string;
  title: string;
  plain_description: string;
  citation?: string;
  statute_ref?: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  status: 'met' | 'unmet' | 'partial' | 'not_applicable';
}

export interface ObligationSetPayload {
  obligation_set_id: string;
  system_id: string;
  obligations: ObligationEntry[];
}

// ============================================================
// Component
// ============================================================

interface ObligationSummaryProductProps {
  payload: ObligationSetPayload | null;
}

export function ObligationSummaryProduct({ payload }: ObligationSummaryProductProps) {
  if (!payload || payload.obligations.length === 0) {
    return (
      <EmptyState
        title="No obligations"
        description="This system has no applicable obligations yet. Complete classification to compute what applies."
      />
    );
  }

  const grouped = groupByFramework(payload.obligations);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {Object.entries(grouped).map(([framework, obligations]) => (
        <section key={framework}>
          <h3 style={{ margin: '0 0 8px', color: 'var(--text)', fontSize: '1rem' }}>
            {framework}
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {obligations.map((ob) => (
              <Card key={ob.obligation_ref}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 600, color: 'var(--text)' }}>
                      {ob.title}
                    </span>
                    <ObligationStatusBadge status={ob.status} />
                  </div>
                  <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                    {ob.plain_description}
                  </p>
                </div>
              </Card>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

// ============================================================
// Helpers
// ============================================================

function ObligationStatusBadge({ status }: { status: ObligationEntry['status'] }) {
  const map: Record<ObligationEntry['status'], { tone: 'neutral' | 'accent'; label: string }> = {
    met: { tone: 'accent', label: 'Met' },
    unmet: { tone: 'neutral', label: 'Unmet' },
    partial: { tone: 'neutral', label: 'Partial' },
    not_applicable: { tone: 'neutral', label: 'N/A' },
  };
  const { tone, label } = map[status];
  return <Badge tone={tone}>{label}</Badge>;
}

function groupByFramework(
  obligations: ObligationEntry[],
): Record<string, ObligationEntry[]> {
  const result: Record<string, ObligationEntry[]> = {};
  for (const ob of obligations) {
    (result[ob.framework] ??= []).push(ob);
  }
  return result;
}
